<script setup lang="ts">
  import { computed } from 'vue';
  import { TabsMenu } from '@/shared/ui';
  import type { FillLogsResponseView } from '../model/fill-logs-timeline';
  import type { FillLogsView } from '../model/useFillLogsReading';

  const props = defineProps<{
    title: string;
    recordLabel: string;
    current: FillLogsResponseView | null;
    total: number;
    isLatest: boolean;
    view: FillLogsView;
  }>();

  const emit = defineEmits<{
    (e: 'update:view', value: FillLogsView): void;
    (e: 'latest'): void;
  }>();

  const VIEW_TABS: ReadonlyArray<{ id: string; text: string; view: FillLogsView }> = [
    { id: 'fill-logs-view-form', text: 'Registro', view: 'form' },
    { id: 'fill-logs-view-changes', text: 'Mudanças', view: 'changes' },
  ];
  const tabOptions = VIEW_TABS.map(({ id, text }) => ({ id, text }));
  const selectedTab = computed(() => VIEW_TABS.find((tab) => tab.view === props.view)?.id ?? VIEW_TABS[0].id);

  function onTab(id: string): void {
    const tab = VIEW_TABS.find((candidate) => candidate.id === id);
    if (tab) emit('update:view', tab.view);
  }

  const position = computed(() => {
    const current = props.current;
    if (!current) return '';
    if (current.kind === 'erp') {
      return `Registro ${props.recordLabel}. Você está vendo os dados do aparelho preenchidos pelo ${current.authorName} em ${current.instant}.`;
    }
    const latest = props.isLatest ? ', a mais recente' : '';
    return `Registro ${props.recordLabel}. Você está vendo a resposta ${current.number} de ${props.total}${latest}, de ${current.authorName} em ${current.instant}.`;
  });
</script>

<template>
  <div class="flex shrink-0 flex-col gap-1 pt-4 pb-2.5 pl-6">
    <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <h1
        class="min-w-0 flex-[1_1_240px] truncate text-left text-xl/7.5 font-semibold text-gray-07"
        :title="title"
        data-testid="fill-logs-title"
      >
        {{ title }}
      </h1>
      <div class="flex items-center gap-2">
        <slot name="tools" />
        <TabsMenu
          v-if="current"
          :options="tabOptions"
          :selected-option="selectedTab"
          role="group"
          aria-label="Como ver a resposta"
          data-testid="fill-logs-view-tabs"
          @option-selected="onTab"
        />
      </div>
    </div>
    <div
      v-if="current"
      class="min-w-0 text-[13px]/[18px] text-gray-03"
    >
      <p
        class="line-clamp-2"
        :title="position"
      >
        <span
          aria-live="polite"
          data-testid="fill-logs-position"
        >
          <template v-if="current.kind === 'erp'">
            Registro {{ recordLabel }}. Você está vendo os
            <span class="font-medium text-gray-07">dados do aparelho</span>
            preenchidos pelo {{ current.authorName }} em {{ current.instant }}.
          </template>
          <template v-else>
            Registro {{ recordLabel }}. Você está vendo a
            <span class="font-medium text-gray-07">resposta {{ current.number }} de {{ total }}</span>{{ isLatest ? ', a mais recente' : '' }},
            de {{ current.authorName }} em {{ current.instant }}.
          </template>
        </span>
        <button
          v-if="!isLatest"
          type="button"
          class="ml-1 font-medium text-[var(--theme-secondary-ink)] hover:underline hover:underline-offset-2 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--theme-secondary-ink)]"
          data-testid="fill-logs-latest"
          @click="emit('latest')"
        >
          Ver a mais recente
        </button>
      </p>
    </div>
  </div>
</template>
